/**
 * FulfillmentRecipientsEditor
 * ---------------------------
 * Add / edit the email addresses that every fulfillment notification for this
 * order goes to. Any number of rows can be marked To (primary); the rest are CC.
 *
 * GET  /api/admin/backorders/{id}/recipients
 * PUT  /api/admin/backorders/{id}/recipients
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const blankRow = () => ({ name: "", email: "", role: "cc" });

export default function FulfillmentRecipientsEditor({
  backorderId, token, onSaved,
  // Work orders reuse this editor against /admin/work-orders/{id}/recipients
  baseUrl,
  blurb,
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const url = `${baseUrl || `${API}/api/admin/backorders/${backorderId}`}/recipients`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(url, { headers });
      const saved = r.data?.recipients || [];
      const list = saved.length > 0 ? saved : (r.data?.suggested || r.data?.suggestions || []);
      setRows(list.length > 0 ? list : [{ ...blankRow(), role: "primary" }]);
      setDirty(saved.length === 0 && list.length > 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load recipients");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token]);
  useEffect(() => { load(); }, [load]);

  const setRow = (i, patch) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const addRow = () => { setRows((rs) => [...rs, blankRow()]); setDirty(true); };
  const removeRow = (i) => {
    setRows((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));
    setDirty(true);
  };
  const toggleRole = (i) => {
    setRows((rs) => {
      const next = rs.map((r, idx) => (
        idx === i ? { ...r, role: r.role === "primary" ? "cc" : "primary" } : r));
      // Never leave the To line empty.
      if (!next.some((r) => r.role === "primary")) next[i] = { ...next[i], role: "primary" };
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    const clean = rows
      .map((r) => ({ id: r.id, name: (r.name || "").trim(), email: (r.email || "").trim(), role: r.role || "cc" }))
      .filter((r) => r.email);
    const bad = clean.find((r) => !r.email.includes("@"));
    if (bad) { toast.error(`"${bad.email}" is not a valid email address.`); return; }
    if (clean.length === 0) { toast.error("Add at least one email address."); return; }
    if (!clean.some((r) => r.role === "primary")) clean[0].role = "primary";
    setSaving(true);
    try {
      const r = await axios.put(url, { recipients: clean }, { headers });
      setRows(r.data?.recipients || clean);
      setDirty(false);
      toast.success(`${r.data?.count ?? clean.length} recipient${(r.data?.count ?? clean.length) === 1 ? "" : "s"} saved — all fulfillment emails use this list.`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save recipients");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="fulfillment-recipients-editor">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-600" />
            Notification Recipients
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {blurb || "Every order-fulfillment email (acknowledgment, shipped, delayed, arrived) goes to these addresses."}{" "}
            Tap the <strong>To / CC</strong> button on a row to switch it &mdash; you can have as many{" "}
            <strong>To</strong> recipients as you need, and everyone else is CC&rsquo;d.
          </p>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || loading || !dirty}
          className="bg-slate-900 hover:bg-slate-800 shrink-0"
          data-testid="recipients-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          {dirty ? "Save recipients" : "Saved"}
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading recipients…
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`recipient-row-${i}`}>
              <Input
                value={r.name || ""}
                onChange={(e) => setRow(i, { name: e.target.value })}
                placeholder="Name (optional)"
                className="col-span-3 text-sm h-9"
                data-testid={`recipient-name-${i}`}
              />
              <Input
                value={r.email || ""}
                onChange={(e) => setRow(i, { email: e.target.value })}
                placeholder="name@hospital.org"
                className="col-span-6 text-sm h-9"
                data-testid={`recipient-email-${i}`}
              />
              <button
                type="button"
                onClick={() => toggleRole(i)}
                className={`col-span-2 h-9 rounded-md text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                  r.role === "primary"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                }`}
                title={r.role === "primary"
                  ? "On the To line — tap to move to CC"
                  : "CC'd — tap to add to the To line"}
                data-testid={`recipient-primary-${i}`}
              >
                {r.role === "primary" ? "To" : "CC"}
              </button>
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={rows.length === 1}
                className="col-span-1 h-9 text-rose-600 hover:bg-rose-50 disabled:text-slate-300 disabled:hover:bg-transparent rounded inline-flex items-center justify-center"
                title="Remove"
                data-testid={`recipient-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="recipient-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add email address
          </Button>
        </div>
      )}
    </div>
  );
}
